import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Clock, Target, ListChecks, Users, BadgeCheck, Download, Euro, Linkedin, Link2, Share2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { VideoPresentation } from "@/components/VideoPresentation";
import { Stars } from "@/components/Stars";
import { api, formatApiError } from "@/lib/api";

const badgeStyle = (s) =>
  s === "Réglementée"
    ? { background: "var(--orange)", color: "var(--bone)" }
    : s === "Habilitation requise"
    ? { background: "var(--ink)", color: "var(--bone)" }
    : { background: "transparent", color: "var(--ink-2)", border: "1px solid var(--border)" };

export default function FormationDetail() {
  const { id } = useParams();
  const [f, setF] = useState(null);
  const [error, setError] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "" });
  const [loading, setLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [rf, setRf] = useState({ name: "", rating: 5, comment: "" });
  const [rSubmitting, setRSubmitting] = useState(false);

  const loadFormation = () => api.get(`/formations/${id}`).then((r) => setF(r.data)).catch(() => setError(true));
  const loadReviews = () => api.get(`/formations/${id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});

  useEffect(() => {
    window.scrollTo(0, 0);
    loadFormation();
    loadReviews();
  }, [id]);

  const submitReview = async (e) => {
    e.preventDefault();
    setRSubmitting(true);
    try {
      await api.post(`/formations/${id}/reviews`, rf);
      toast.success("Merci pour votre avis !");
      setRf({ name: "", rating: 5, comment: "" });
      await loadFormation();
      await loadReviews();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setRSubmitting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/registrations", { ...form, formation_id: f.id, formation_title: f.title });
      toast.success("Inscription enregistrée ! Nous vous recontactons rapidement.");
      setForm({ name: "", email: "", phone: "", company: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (error) return (
    <div className="pt-40 pb-40 text-center" data-testid="formation-not-found">
      <p className="font-display text-3xl mb-6">Formation introuvable</p>
      <Link to="/formations" className="btn-primary px-6 py-3 font-semibold">Retour aux formations</Link>
    </div>
  );
  if (!f) return <div className="pt-[148px] min-h-screen" />;

  const objectifs = (f.objectives && f.objectives.length) ? f.objectives : [
    `Comprendre les enjeux et le cadre lié à « ${f.title} »`,
    "Maîtriser les bonnes pratiques et la réglementation applicable",
    "Savoir mettre en œuvre les acquis dans son environnement de travail",
  ];
  const programme = (f.programme && f.programme.length) ? f.programme : [
    "Introduction et contexte réglementaire",
    "Apports théoriques et méthodologiques",
    "Études de cas et mises en situation pratiques",
    "Plan d'action personnalisé et évaluation des acquis",
  ];

  return (
    <div className="pt-[148px]" data-testid="formation-detail-page">
      <div className="max-w-[1100px] mx-auto px-5 md:px-10 pt-10">
        <Link to="/formations" className="link-hover inline-flex items-center gap-2 text-sm font-medium" data-testid="back-formations">
          <ArrowLeft size={16} /> Retour aux formations
        </Link>
      </div>

      <section className="max-w-[1100px] mx-auto px-5 md:px-10 py-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <Reveal>
            <span className="overline" style={{ color: "var(--orange)" }}>{f.domain}</span>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-tight mt-4">{f.title}</h1>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <span className="flex items-center gap-2 text-sm px-3 py-1.5" style={{ border: "1px solid var(--border)" }}><Clock size={15} /> {f.duration}</span>
              <span className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold px-3 py-1.5" style={badgeStyle(f.status)}><BadgeCheck size={14} /> {f.status}</span>
              <span className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold px-3 py-1.5" style={{ background: "var(--orange)", color: "var(--bone)" }} data-testid="opco-badge"><Euro size={14} /> Finançable OPCO</span>
              {f.rating_count > 0 && (
                <span className="flex items-center gap-2 text-sm" data-testid="formation-rating">
                  <Stars value={f.rating_avg} size={15} />
                  <span style={{ color: "var(--ink-2)" }}>{f.rating_avg.toFixed(1)} · {f.rating_count} avis</span>
                </span>
              )}
            </div>

            <VideoPresentation endpoint={`/formations/${f.id}`} />

            <div className="mt-4 flex items-center gap-2" data-testid="share-buttons">
              <span className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold mr-1" style={{ color: "var(--ink-2)" }}><Share2 size={13} /> Partager</span>
              <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer"
                className="p-2 transition-colors hover:opacity-70" style={{ border: "1px solid var(--border)" }} aria-label="Partager sur LinkedIn" data-testid="share-linkedin-btn">
                <Linkedin size={16} style={{ color: "#0A66C2" }} />
              </a>
              <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(`Formation « ${f.title} » — ALDOL-SOLUTIONS`)}`} target="_blank" rel="noopener noreferrer"
                className="p-2 transition-colors hover:opacity-70" style={{ border: "1px solid var(--border)" }} aria-label="Partager sur X" data-testid="share-x-btn">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
              </a>
              <button onClick={async () => { try { await navigator.clipboard.writeText(window.location.href); toast.success("Lien copié !"); } catch { toast.info("Copiez le lien depuis la barre d'adresse."); } }}
                className="p-2 transition-colors hover:opacity-70" style={{ border: "1px solid var(--border)" }} aria-label="Copier le lien" data-testid="share-copy-btn">
                <Link2 size={16} />
              </button>
            </div>

            {f.image && (
              <div className="mt-8 overflow-hidden" style={{ border: "1px solid var(--border)" }}>
                <img src={f.image} alt={f.title} className="w-full h-64 sm:h-80 object-cover" data-testid="formation-hero-image" />
              </div>
            )}
            {f.description && <p className="mt-8 text-lg leading-relaxed" style={{ color: "var(--ink-2)" }} data-testid="formation-description">{f.description}</p>}

            <div className="mt-10">
              <h2 className="flex items-center gap-2 font-display font-bold text-2xl mb-4"><Target size={22} style={{ color: "var(--orange)" }} /> Objectifs</h2>
              <ul className="space-y-2">{objectifs.map((o) => <li key={o} className="flex gap-3" style={{ color: "var(--ink-2)" }}><span style={{ color: "var(--orange)" }}>—</span> {o}</li>)}</ul>
            </div>
            <div className="mt-10">
              <h2 className="flex items-center gap-2 font-display font-bold text-2xl mb-4"><ListChecks size={22} style={{ color: "var(--orange)" }} /> Programme</h2>
              <ul className="space-y-2">{programme.map((p, i) => <li key={p} className="flex gap-3" style={{ color: "var(--ink-2)" }}><span className="font-mono2" style={{ color: "var(--orange)" }}>{String(i + 1).padStart(2, "0")}</span> {p}</li>)}</ul>
            </div>
            <div className="mt-10">
              <h2 className="flex items-center gap-2 font-display font-bold text-2xl mb-4"><Users size={22} style={{ color: "var(--orange)" }} /> Public & prérequis</h2>
              <p style={{ color: "var(--ink-2)" }}>Tout collaborateur concerné par le domaine « {f.domain} ». Aucun prérequis particulier, sauf mention contraire liée au référentiel applicable{f.status !== "Librement commercialisable" ? " (formation réglementée)" : ""}.</p>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-5">
          <Reveal delay={0.1}>
            <form onSubmit={submit} className="p-6 md:p-8 lg:sticky lg:top-24" style={{ background: "var(--white)", border: "1px solid var(--border)" }} data-testid="formation-reg-form">
              <h3 className="font-display font-bold text-xl mb-1">S'inscrire à cette formation</h3>
              <p className="text-sm mb-5" style={{ color: "var(--ink-2)" }}>Contenu détaillé et devis sur demande.</p>
              <input className="field mb-3" placeholder="Nom complet *" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="reg-name" />
              <input className="field mb-3" type="email" placeholder="Email *" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="reg-email" />
              <input className="field mb-3" placeholder="Téléphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="reg-phone" />
              <input className="field mb-4" placeholder="Entreprise" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="reg-company" />
              <button type="submit" disabled={loading} className="btn-primary w-full py-3 font-semibold" data-testid="reg-submit">
                {loading ? "Envoi..." : "Demander mon inscription"}
              </button>
            </form>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/formations/${f.id}/pdf`} target="_blank" rel="noopener noreferrer"
              className="btn-outline mt-3 w-full py-3 font-semibold flex items-center justify-center gap-2" data-testid="download-pdf-btn">
              <Download size={16} /> Télécharger le programme (PDF)
            </a>
          </Reveal>
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-5 md:px-10 pb-24" data-testid="formation-reviews-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10" style={{ borderTop: "1px solid var(--border)", paddingTop: "3rem" }}>
          <div className="lg:col-span-7">
            <h2 className="font-display font-black text-3xl mb-2">Avis des participants</h2>
            <div className="flex items-center gap-3 mb-8">
              {f.rating_count > 0 ? (
                <>
                  <Stars value={f.rating_avg} size={20} />
                  <span style={{ color: "var(--ink-2)" }}>{f.rating_avg.toFixed(1)} / 5 · {f.rating_count} avis</span>
                </>
              ) : (
                <span style={{ color: "var(--ink-2)" }}>Aucun avis pour le moment. Soyez le premier !</span>
              )}
            </div>
            <div className="space-y-px" style={{ background: reviews.length ? "var(--border)" : "transparent", border: reviews.length ? "1px solid var(--border)" : "none" }}>
              {reviews.map((r) => (
                <div key={r.id} className="p-5" style={{ background: "var(--white)" }} data-testid={`formation-review-${r.id}`}>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <span className="font-semibold">{r.name}</span>
                    <Stars value={r.rating} size={14} />
                  </div>
                  {r.comment && <p style={{ color: "var(--ink-2)" }}>{r.comment}</p>}
                  <p className="text-xs font-mono2 mt-2" style={{ color: "var(--ink-2)" }}>{(r.created_at || "").slice(0, 10)}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <form onSubmit={submitReview} className="p-6 md:p-8" style={{ background: "var(--white)", border: "1px solid var(--border)" }} data-testid="formation-review-form">
              <h3 className="font-display font-bold text-xl mb-5">Noter cette formation</h3>
              <div className="mb-4">
                <p className="overline mb-2" style={{ color: "var(--ink-2)" }}>Votre note</p>
                <Stars value={rf.rating} size={28} interactive onChange={(n) => setRf({ ...rf, rating: n })} />
              </div>
              <input className="field mb-3" placeholder="Votre nom *" required value={rf.name} onChange={(e) => setRf({ ...rf, name: e.target.value })} data-testid="formation-review-name" />
              <textarea className="field mb-4" rows={4} placeholder="Votre retour d'expérience" value={rf.comment} onChange={(e) => setRf({ ...rf, comment: e.target.value })} data-testid="formation-review-comment" />
              <button type="submit" disabled={rSubmitting} className="btn-primary w-full py-3 font-semibold" data-testid="formation-review-submit">
                {rSubmitting ? "Envoi…" : "Publier mon avis"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
